import { useState } from 'react'
import type { FormEvent } from 'react'
import type { ReactNode } from 'react'
import { Button, Checkbox, Field, cx } from '../../ui'
import { PROVIDERS } from '../flow'
import type { Provider, ProviderId } from '../flow'

export interface SignInProps {
  email: string
  company: string
  onEmailChange: (value: string) => void
  onCompanyChange: (value: string) => void
  onProvider: (provider: ProviderId) => void
  onMagicLink: () => void
  /** A message from a failed round trip, e.g. an Entra callback that was refused. */
  notice?: ReactNode
  /**
   * 'info' for a confirmation ("You are signed out"), shown neutrally and announced politely.
   * Every notice used to be the rose error box with role=alert, so a sign-out that WORKED read
   * as a failure.
   */
  noticeTone?: 'warn' | 'info'
  /** Disables submission while a request is in flight. */
  busy?: boolean
  /**
   * The SSO buttons to draw. The prototype draws every designed provider, disabled where it is not
   * built; the real sign-in page passes AVAILABLE_PROVIDERS, so a customer sees only what works.
   */
  providers?: readonly Provider[]
  /**
   * The bot check (Cloudflare Turnstile), drawn above the submit button when it is on. Passed in rather
   * than built here because this screen is shared with the prototype, which has no such check.
   */
  challenge?: ReactNode
  /** The check is on and has not been passed yet: the link cannot be requested. */
  challengePending?: boolean
  /**
   * Why the button is disabled, in words, when the cause is the check ("Complete the security check above
   * to continue"). A disabled button with no stated reason is a dead end for a keyboard or screen-reader
   * user, so it is announced politely and tied to the button.
   */
  challengeHint?: string | null
}

/**
 * Sign in — the SSO buttons, then the magic-link form.
 *
 * The order is the security decision on this screen. Entra sits first and is the only bordered
 * option because it identifies the customer's tenant immediately, which every later screen
 * depends on.
 *
 * It also sidesteps a delivery risk. Our first production message to a Microsoft 365 tenant was
 * quarantined as Phish/High with SPF, DKIM and DMARC all passing — first-contact sender
 * reputation, not authentication. That is one observation rather than a rule (filtering depends
 * on each tenant's anti-spam and anti-phishing policy), but it is enough to make the magic link
 * the fallback rather than the headline: a customer who signs in with Entra never has to
 * discover it.
 *
 * THE COPY SAYS WHO EACH ROUTE IS FOR (2026-10-04). The order stays; what changed is what the page
 * claims. Microsoft sign-in cannot start an account -- only a mailbox does (migration 0104) -- so
 * telling a first-time visitor that it was "Recommended" sent them to an entra_not_linked error. The
 * intro now says a new account starts with the email link, and the Microsoft button says it is for
 * accounts that already have a Microsoft account connected.
 */
export function SignIn({
  email,
  company,
  onEmailChange,
  onCompanyChange,
  onProvider,
  onMagicLink,
  notice,
  noticeTone = 'warn',
  busy = false,
  providers = PROVIDERS,
  challenge,
  challengePending = false,
  challengeHint = null,
}: SignInProps) {
  const [agreed, setAgreed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The design disables the button until both fields have content, then validates properly on
  // submit. Keeping validation on submit rather than per keystroke means the message appears
  // once, when the person has finished typing, instead of accusing them mid-word.
  const submittable = email.trim().length > 0 && company.trim().length > 0

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (!email.includes('@')) return setError('Enter your email address.')
    if (!company.trim()) return setError('Tell us your company name.')
    if (!agreed) return setError('Please accept the Terms of Service and the Privacy notice to continue.')
    // Defence in depth. The button is disabled while this is true, and a browser does not submit a form by
    // Enter while its default button is disabled, so this is only reached by a submit that bypasses the
    // button (a test, a script, an assistive technology).
    if (challengePending) return setError('Please complete the security check first.')

    setError(null)
    onMagicLink()
  }

  return (
    <div className="ob-screen ob-screen-narrow rise-in">
      <p className="ob-eyebrow">Welcome</p>
      <h2>Sign in to Defenssive</h2>
      <p className="text-muted" style={{ fontSize: 14, marginBottom: 'var(--space-6)' }}>
        No password to create. New to Defenssive? Use your work email and the link below to start. Already
        connected a Microsoft account? Sign in with it. Each organization gets its own isolated
        workspace, keyed to its Entra tenant ID.{' '}
        {/* Plain <a>, like the Terms and Privacy links below: Caddy serves /product as a static page
            outside the application. It is where an administrator can read exactly which permissions
            the product asks for before approving them. */}
        <a href="/product" target="_blank" rel="noopener">
          What does Defenssive read?
        </a>
      </p>

      {/* A failed Entra round trip lands back here. role=alert so it is announced rather than
          only appearing above a form the person is about to retype. */}
      {notice ? (
        <div
          className={noticeTone === 'info' ? 'sign-in-notice is-info' : 'sign-in-notice'}
          role={noticeTone === 'info' ? 'status' : 'alert'}
        >
          {notice}
        </div>
      ) : null}

      <div className="sso-list">
        {providers.map((provider) => (
          <button
            key={provider.id}
            type="button"
            className={cx(
              'sso-button',
              provider.id === 'entra' && provider.available && 'is-recommended',
              !provider.available && 'is-unavailable',
            )}
            // Without this the computed name is all three fragments run together — "Microsoft
            // Entra ID Recommended — identifies your tenant immediately OIDC". The note and the
            // protocol tag are still read as content; they just stop being the button's name.
            aria-label={
              provider.available
                ? `Sign in with ${provider.name}`
                : `${provider.name} — not available yet`
            }
            // Disabled rather than hidden, and it really is disabled: a sign-in button that
            // appears to work and does not is the same class of problem as a fabricated score.
            disabled={!provider.available || busy}
            onClick={() => onProvider(provider.id)}
          >
            <span className="sso-mark">
              <provider.Mark />
            </span>
            <span className="sso-text">
              <span className="sso-name">{provider.name}</span>
              <span className="sso-note">
                {provider.available ? provider.note : 'Not available yet'}
              </span>
            </span>
            <span className="label-mono sso-protocol">{provider.protocol}</span>
          </button>
        ))}
      </div>

      <div className="or-divider">new here? use an email link</div>

      <form className="magic-form" onSubmit={handleSubmit} noValidate>
        <Field
          label="Email address"
          type="email"
          autoComplete="email"
          placeholder="john@company.com"
          value={email}
          onChange={(event) => {
            onEmailChange(event.target.value)
            setError(null)
          }}
        />
        <p className="form-footnote">
          New self-service accounts require a work email. Known personal and temporary email
          domains cannot register. Already registered or invited by a workspace owner? Use your
          existing or invited address, including a personal address.
        </p>
        <Field
          label="Company name"
          type="text"
          autoComplete="organization"
          placeholder="ABC Manufacturing"
          value={company}
          onChange={(event) => {
            onCompanyChange(event.target.value)
            setError(null)
          }}
        />
        {/* Agreement to documents a person cannot open is not agreement. Plain <a>, not a router
            Link: Caddy serves /terms and /privacy as static pages outside the application. */}
        <Checkbox
          label={
            <>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener">Terms of Service</a> and the{' '}
              <a href="/privacy" target="_blank" rel="noopener">Privacy notice</a>
            </>
          }
          checked={agreed}
          onChange={(event) => {
            setAgreed(event.target.checked)
            setError(null)
          }}
        />
        {challenge}
        {challengeHint ? (
          <p id="challenge-hint" className="form-footnote" role="status">
            {challengeHint}
          </p>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          block
          disabled={!submittable || busy || challengePending}
          aria-describedby={challengeHint ? 'challenge-hint' : undefined}
        >
          {busy ? 'Sending…' : 'Email me a sign-in link'}
        </Button>

        {/* role=alert so the message is announced when it appears, not just coloured. */}
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="form-footnote">
          {/* It also promised "tenant-wide SSO enforcement" once connected. No such setting exists. */}
          Links are single-use and expire in 15 minutes.
        </div>
      </form>
    </div>
  )
}
